import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SitemapPage = () => {
  const [serviceAreas, setServiceAreas] = useState({ cities: [], services: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        const [citiesRes, servicesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-areas/cities`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/service-areas/services`)
        ]);
        const citiesData = await citiesRes.json();
        const servicesData = await servicesRes.json();
        setServiceAreas({
          cities: citiesData.cities || [],
          services: servicesData.services || []
        });
      } catch (error) {
        console.error('Error fetching service areas:', error);
      }
      setLoading(false);
    };
    fetchServiceAreas();
  }, []);

  const dentalServices = [
    { name: 'Dental Implants', path: '/dental-implants' },
    { name: 'Invisalign', path: '/invisalign' },
    { name: 'LANAP Laser Treatment', path: '/lanap' },
    { name: 'Cosmetic Dentistry', path: '/cosmetic-dentistry' },
    { name: 'Emergency Dentistry', path: '/emergency-dentist' },
    { name: 'Bone Grafting', path: '/bone-grafting' },
    { name: 'Bruxism / Teeth Grinding', path: '/bruxism-teeth-grinding' },
    { name: 'Composite Fillings', path: '/composite-fillings' },
    { name: 'Crowns & Bridges', path: '/crowns-bridges' },
    { name: 'Dentures', path: '/dentures' },
    { name: 'Gingivectomy', path: '/gingivectomy' },
    { name: 'Guided Tissue Bone Regeneration', path: '/guided-tissue-bone-regeneration' },
    { name: 'Gum Disease Treatment', path: '/gum-disease' },
    { name: 'Laser Dentistry', path: '/laser-dentistry' },
    { name: 'Occlusal (Bite) Adjustment', path: '/occlusal-bite-adjustment' },
    { name: 'Oral Cancer Screening', path: '/oral-cancer-screening' },
    { name: 'Osseous Surgery', path: '/osseous-surgery' },
    { name: 'Porcelain Veneers', path: '/porcelain-veneers' },
    { name: 'Preventative Dentistry', path: '/preventative-dentistry' },
    { name: 'Ridge Augmentation', path: '/ridge-augmentation' },
    { name: 'Ridge Preservation', path: '/ridge-preservation' },
    { name: 'Root Canals', path: '/root-canals' },
    { name: 'Scaling & Root Planing', path: '/scaling-root-planing' },
    { name: 'Sedation Dentistry', path: '/sedation' },
    { name: 'Teeth Whitening', path: '/teeth-whitening' },
  ];

  const specials = [
    { name: 'All Specials', path: '/specials' },
    { name: 'Dental Implants Free Consultation', path: '/dental-implants-free-consult-with-x-rays' },
    { name: 'Invisalign Free Consultation', path: '/invisalign-free-consultation' },
    { name: '$99 Emergency Exam', path: '/specials' },
    { name: '$199 Healthy Cleaning', path: '/specials' },
    { name: 'Free LANAP Consultation', path: '/specials' },
  ];

  const mainPages = [
    { name: 'Home', path: '/' },
    { name: 'Top Dentists in Fort Worth', path: '/top-dentists-fort-worth' },
    { name: 'Dental Implants Fort Worth', path: '/dental-implants-fort-worth' },
    { name: 'About Us / Our Team', path: '/our-team' },
    { name: 'Our Offices', path: '/our-offices' },
    { name: 'Dental Implant Center', path: '/implant-center' },
    { name: 'Areas We Serve', path: '/areas-we-serve' },
    { name: 'Financing', path: '/form-finances' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Dental News & Tips', path: '/blog' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const legalPages = [
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'HIPAA Notice of Privacy Practices', path: '/hipaa-npp' },
  ];

  // Service area services for the sitemap
  const serviceAreaServices = [
    { name: 'Dental Implants', slug: 'dental-implants' },
    { name: 'Invisalign', slug: 'invisalign' },
    { name: 'LANAP Gum Treatment', slug: 'lanap-gum-treatment' },
    { name: 'Cosmetic Dentistry', slug: 'cosmetic-dentistry' },
    { name: 'Emergency Dentist', slug: 'emergency-dentist' },
    { name: 'General Dentistry', slug: 'general-dentistry' },
    { name: 'Teeth Whitening', slug: 'teeth-whitening' },
    { name: 'Porcelain Veneers', slug: 'porcelain-veneers' },
    { name: 'Crowns & Bridges', slug: 'crowns-bridges' },
    { name: 'Dentures', slug: 'dentures' },
    { name: 'Root Canals', slug: 'root-canals' },
  ];

  return (
    <>
      <Helmet>
        <title>Sitemap | Fort Worth Dental - All Pages & Services</title>
        <meta name="description" content="Complete sitemap of Fort Worth Dental website. Browse all dental services, 37 service area cities, specials, and informational pages." />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold">Website Sitemap</h1>
            <p className="text-lg mt-2">Navigate all {469}+ pages on our website</p>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Main Pages */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-teal-600 mb-4 border-b pb-2">Main Pages</h2>
                <ul className="space-y-2">
                  {mainPages.map((page) => (
                    <li key={page.path}>
                      <Link to={page.path} className="text-gray-700 hover:text-teal-600 hover:underline">
                        {page.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Dental Services */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-teal-600 mb-4 border-b pb-2">Dental Services ({dentalServices.length})</h2>
                <ul className="space-y-2 max-h-80 overflow-y-auto">
                  {dentalServices.map((service) => (
                    <li key={service.path}>
                      <Link to={service.path} className="text-gray-700 hover:text-teal-600 hover:underline text-sm">
                        {service.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Specials */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-teal-600 mb-4 border-b pb-2">Special Offers</h2>
                <ul className="space-y-2">
                  {specials.map((special, index) => (
                    <li key={index}>
                      <Link to={special.path} className="text-gray-700 hover:text-teal-600 hover:underline">
                        {special.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal Pages */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-teal-600 mb-4 border-b pb-2">Legal & Compliance</h2>
                <ul className="space-y-2">
                  {legalPages.map((page) => (
                    <li key={page.path}>
                      <Link to={page.path} className="text-gray-700 hover:text-teal-600 hover:underline">
                        {page.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Service Area Pages - Full Width */}
            <div className="mt-8 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-teal-600 mb-4 border-b pb-2">
                Service Area Pages ({serviceAreas.cities.length} Cities × {serviceAreaServices.length} Services = {serviceAreas.cities.length * serviceAreaServices.length} Pages)
              </h2>
              <p className="text-gray-600 mb-4">
                We proudly serve patients in 53 cities across the DFW metroplex and Western Texas. Click on a city to see all services available in that area.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {serviceAreas.cities.map((city) => (
                  <div key={city.slug} className="text-sm">
                    <span className="font-medium text-gray-800">{city.name}</span>
                    <ul className="mt-1 space-y-0.5">
                      {serviceAreaServices.slice(0, 3).map((service) => (
                        <li key={`${service.slug}-${city.slug}`}>
                          <Link 
                            to={`/${service.slug}-${city.slug}`}
                            className="text-gray-500 hover:text-teal-600 text-xs"
                          >
                            {service.name}
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link 
                          to="/areas-we-serve"
                          className="text-teal-600 hover:underline text-xs"
                        >
                          +{serviceAreaServices.length - 3} more...
                        </Link>
                      </li>
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* XML Sitemaps for Search Engines */}
            <div className="mt-8 bg-teal-50 rounded-lg p-6">
              <h2 className="text-xl font-bold text-teal-700 mb-4">XML Sitemaps (For Search Engines)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <a 
                  href="/api/sitemap-index.xml" 
                  className="flex items-center gap-2 text-teal-600 hover:text-teal-800 hover:underline"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  📄 Sitemap Index (Master)
                </a>
                <a 
                  href="/api/sitemap.xml" 
                  className="flex items-center gap-2 text-teal-600 hover:text-teal-800 hover:underline"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  📄 Main Pages Sitemap
                </a>
                <a 
                  href="/api/service-areas-sitemap.xml" 
                  className="flex items-center gap-2 text-teal-600 hover:text-teal-800 hover:underline"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  📄 Service Areas Sitemap (407 pages)
                </a>
                <a 
                  href="/api/video-sitemap.xml" 
                  className="flex items-center gap-2 text-teal-600 hover:text-teal-800 hover:underline"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  🎬 Video Sitemap (10 videos)
                </a>
                <a 
                  href="/api/image-sitemap.xml" 
                  className="flex items-center gap-2 text-teal-600 hover:text-teal-800 hover:underline"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  🖼️ Image Sitemap (91 images)
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SitemapPage;
